import { Flex, Link, Stack, Text } from '@chakra-ui/react';
import { Link as RouteLink, useNavigate } from 'react-router-dom';
import { MouseEvent } from 'react';
import { useAppContext, useLogout } from '@frx/frx-react-context';

import cn from 'classnames';
import { standardizeUrl } from '@frx/frx-services';
import styles from './Footer.module.scss';
import { getDataTracking } from '../../../utils';
import { setEvent } from '../../../analytics';

export const Footer = () => {
  const {
    data: {
      authData: { loggedIn },
    },
  } = useAppContext();
  const { mutate: logout } = useLogout();
  const navigateTo = useNavigate();

  const useFrxAuthentication =
    process.env.REACT_APP_USE_FRX_AUTHENTICATION === 'true';

  const handleLogout = () => {
    if (useFrxAuthentication) {
      // TODO: Add frid to session to be captured in session_logout
      const redirectUrl = standardizeUrl(
        `/site/SPageServer/?pagename=session_logout`
      );
      window.location.href = standardizeUrl(
        `site/UserLogin?logout=true&NEXTURL=${redirectUrl}`
      );
    } else {
      logout(undefined, {
        onSuccess: () => {
          setEvent({ pathname: '/logout' });
          navigateTo('/login');
        },
      });
    }
  };
  const currentYear = new Date().getFullYear();
  const handleOptOutClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,no-undef
    const window = globalThis as any;
    if (
      window?.OneTrust &&
      typeof window?.OneTrust?.ToggleInfoDisplay === 'function'
    ) {
      window.OneTrust.ToggleInfoDisplay();
    }
  };

  return (
    <Flex
      fontSize="12px"
      alignItems="center"
      direction="column"
      w="100%"
      bg={['brand.monochromatic.900']}
      px="2rem"
      py="3rem"
      className={cn(styles.container)}
    >
      <Stack
        direction={['column', 'row']}
        spacing={['1rem', '1.5rem']}
        mb="1.5rem"
        align="center"
      >
        <Link as={RouteLink} to="/" {...getDataTracking('footerHomeLink')}>
          Home
        </Link>

        <Link
          as={RouteLink}
          to="/contactus"
          {...getDataTracking('footerContactLink')}
        >
          Contact Us
        </Link>

        {loggedIn ? (
          <Link
            as={RouteLink}
            onClick={handleLogout}
            {...getDataTracking('footerLogoutLink')}
          >
            Logout
          </Link>
        ) : (
          <Link
            as={RouteLink}
            to="/login"
            {...getDataTracking('footerLoginLink')}
          >
            Log In
          </Link>
        )}
      </Stack>

      <Text mb="1rem" color="brand.monochromatic.300" align="center">
        &copy; Copyright {currentYear}. St. Jude Children's Research Hospital, a
        not-for-profit, section 501(c)(3).
      </Text>

      <Text mb="1rem" color="brand.monochromatic.300" align="center">
        St. Jude Children's Research Hospital / ALSAC, 501 St. Jude Place,
        Memphis, TN 38105 USA{' '}
        <Link href="mailto:donors@stjude.org">donors@stjude.org</Link>
      </Text>

      <Stack
        direction={['column', 'row']}
        spacing={['1rem', '1.5rem']}
        mb="1.5rem"
        align="center"
      >
        <Link
          href="https://www.stjude.org/legal.html"
          isExternal
          {...getDataTracking('footerLegalLink')}
        >
          Legal
        </Link>

        <Link
          href="https://www.stjude.org/legal/u-s-privacy-policy-statement.html"
          isExternal
          {...getDataTracking('footerPrivacyLink')}
        >
          U.S. Privacy Notice
        </Link>

        <Link
          href="#"
          onClick={handleOptOutClick}
          {...getDataTracking('footerPrivacyOptOutLink')}
        >
          Your Opt-Out Rights
        </Link>

        <Link
          href="https://fundraising.stjude.org/site/SPageServer?pagename=COPPA"
          isExternal
          {...getDataTracking('footerCoppaLink')}
        >
          COPPA
        </Link>

        <Link
          href="https://www.stjude.org/legal/notice-of-non-discrimination.html"
          isExternal
          {...getDataTracking('footerNonDiscriminationLink')}
        >
          Notice of Non-Discrimination
        </Link>

        <Link
          href="https://www.stjude.org/sitemap.html"
          isExternal
          {...getDataTracking('footerSiteMapLink')}
        >
          Site Map
        </Link>
      </Stack>
    </Flex>
  );
};
